import {
  Component,
  Expressions,
  Plugins,
  Types,
  type ExpressionPath,
} from '@packages/systems/wfdl';
import {EElement, type ExpressionElement} from '@packages/systems/wfdl-ast';
import {getDynamoBindingPathsWithExprPath} from '@packages/systems/dynamo/wfdl';
import {getCurrentOrDefaultExpression} from '@packages/systems/core/component-conversions';
import {getDataTypeFromElement} from '@packages/systems/user-components/utils';
import {isComponentInst} from '@packages/utilities/core-elements';
import {isDynamoWrapper} from '@packages/utilities/elementTypeUtils';
import {instantiateExpressionWithUniqueIds} from '@packages/utilities/platform-instantiateExpressionWithUniqueIds';
import {generateGuid} from '@packages/utilities/guid';
import {getSecond} from '@packages/utilities/Pair';
import {unbindReferences} from '@packages/systems/user-components/unbind-references';

// The override that holds a binding: data[propKey] or legacy
// data.sym.overrides[propKey]. The binding itself can sit deeper, e.g. at
// data[propKey].url for a Link prop.
const getOverridePath = (key: ExpressionPath): ExpressionPath | null => {
  const length = key[0]?.in === 'Record' && key[0].at === 'sym' ? 3 : 1;
  const overridePath = key.slice(0, length);
  return overridePath.length === length &&
    overridePath.every((part) => part.in === 'Record')
    ? overridePath
    : null;
};

const unbindKey = (
  element: ExpressionElement,
  key: ExpressionPath,
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType
): ExpressionElement => {
  const data = Expressions.getElementData(element);

  // Dropping the instance override lets the prop's own default apply.
  if (isComponentInst(componentMap)(element)) {
    const overridePath = getOverridePath(key);
    return overridePath
      ? Expressions.setElementData(
          element,
          Expressions.deleteInRecord(data, overridePath)
        )
      : element;
  }

  const dataType = getDataTypeFromElement(plugins)(componentMap)(element);
  const field = Types.__DEPRECATED__getTypeAtExpressionPath(
    dataType,
    key,
    plugins
  );
  if (!field) return element;

  // Same result as "Remove personalization": the element-type default.
  const defaultExpr = getSecond(
    instantiateExpressionWithUniqueIds(
      getCurrentOrDefaultExpression(plugins)(field)
    )(generateGuid())
  );
  return Expressions.setElementData(
    element,
    Expressions.setIn(data, key, defaultExpr)
  );
};

/**
 * Strips page-level CMS (Dynamo) bindings from a pasted or duplicated element
 * tree. On campaign sites each personalized campaign has its own hidden
 * collection, so a copied binding would either share a variant field with the
 * original or point at another campaign's collection.
 *
 * Collection lists are left alone: their bindings resolve against the list's
 * own items, not the page.
 */
export const removeDynamoBindingsOnPaste = (
  element: ExpressionElement,
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType
): ExpressionElement => {
  const root = EElement(element);
  const elements = Object.values(Expressions.allElements(root));

  const skippedIds = new Set<string>();
  for (const el of elements) {
    if (!isDynamoWrapper(el)) continue;
    for (const id of Object.keys(Expressions.allElements(EElement(el)))) {
      skippedIds.add(id);
    }
  }

  let updatedRoot = root;
  for (const el of elements) {
    if (skippedIds.has(el.id)) continue;

    const bindingPaths = getDynamoBindingPathsWithExprPath(
      Expressions.getElementData(el)
    );
    if (bindingPaths.size === 0) continue;

    updatedRoot = Expressions.updateElement(
      el.id,
      (current) => {
        let next = current;
        bindingPaths.forEach((exprPaths) => {
          for (const key of exprPaths) {
            next = unbindKey(next, key, componentMap, plugins);
          }
        });
        return next;
      },
      updatedRoot
    );
  }

  return updatedRoot === root
    ? element
    : (Expressions.getElement(updatedRoot) ?? element);
};

/**
 * Personalizing text inside a component binds the instance's prop override,
 * not the element, so an element copied inside that component only references
 * the prop. Replaces references to CMS-bound props of the focused instance with
 * the prop's default, so the copy doesn't follow the original's CMS field.
 */
export const removeDynamoBoundPropReferencesOnPaste = (
  element: ExpressionElement,
  focusedInstance: ExpressionElement,
  focusedComponent: Component.ComponentType,
  plugins: Plugins.PluginsType
): ExpressionElement => {
  const fields =
    Types.getRecordFields(Component.getDataType(focusedComponent)) ?? {};
  const boundPropKeys = new Set<string>();
  getDynamoBindingPathsWithExprPath(
    Expressions.getElementData(focusedInstance)
  ).forEach((exprPaths) => {
    for (const key of exprPaths) {
      const propKey = getOverridePath(key)?.at(-1);
      if (propKey?.in === 'Record' && propKey.at in fields) {
        boundPropKeys.add(propKey.at);
      }
    }
  });
  if (boundPropKeys.size === 0) return element;

  let updated = EElement(element);
  boundPropKeys.forEach((propKey) => {
    updated = unbindReferences(updated, propKey, focusedComponent, plugins);
  });
  return Expressions.getElement(updated) ?? element;
};
